import React, { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { saveToLocalStorage, loadFromLocalStorage } from '@/utils/helpers';

// Define connection status type
type ConnectionStatus = 'unknown' | 'connecting' | 'connected' | 'failed';

// Define connection status for both source and destination
interface ConnectionInfo {
  status: ConnectionStatus;
  error: string;
  timestamp: string;
}

// Define the context type
interface AppContextType {
  // Common settings
  apiKey: string;
  setApiKey: (key: string) => void;
  
  // Theme settings
  theme: string;
  setTheme: (theme: string) => void;
  
  // Source backend settings
  sourceBackend: string;
  setSourceBackend: (backend: string) => void;
  sourcePort: string;
  setSourcePort: (port: string) => void;
  isCustomSourceBackend: boolean;
  setIsCustomSourceBackend: (isCustom: boolean) => void;
  getSourceBackendUrl: () => string;
  
  // Destination backend settings
  destBackend: string;
  setDestBackend: (backend: string) => void;
  destPort: string;
  setDestPort: (port: string) => void;
  isCustomDestBackend: boolean;
  setIsCustomDestBackend: (isCustom: boolean) => void;
  getDestBackendUrl: () => string;
  
  // Source connection status
  connectionStatus: ConnectionStatus;
  connectionError: string;
  connectionTimestamp: string;
  validateConnection: () => Promise<void>;
  
  // Destination connection status
  destConnectionStatus: ConnectionStatus;
  destConnectionError: string;
  destConnectionTimestamp: string;
  validateDestConnection: () => Promise<void>;
  
  // API utilities
  fetchWithCache: <T>(url: string, options?: RequestInit) => Promise<T>;
  clearApiCache: () => void;
}

// Create the context with default values
const AppContext = createContext<AppContextType>({
  // Common settings
  apiKey: '',
  setApiKey: () => {},
  
  // Theme settings
  theme: 'light',
  setTheme: () => {},
  
  // Source backend settings
  sourceBackend: 'tracking.cityflo.com',
  setSourceBackend: () => {},
  sourcePort: '8080',
  setSourcePort: () => {},
  isCustomSourceBackend: false,
  setIsCustomSourceBackend: () => {},
  getSourceBackendUrl: () => '',
  
  // Destination backend settings
  destBackend: 'tracking.cityflo.com',
  setDestBackend: () => {},
  destPort: '8080',
  setDestPort: () => {},
  isCustomDestBackend: false,
  setIsCustomDestBackend: () => {},
  getDestBackendUrl: () => '',
  
  // Source connection status
  connectionStatus: 'unknown',
  connectionError: '',
  connectionTimestamp: '',
  validateConnection: async () => {},
  
  // Destination connection status
  destConnectionStatus: 'unknown',
  destConnectionError: '',
  destConnectionTimestamp: '',
  validateDestConnection: async () => {},
  
  // API utilities
  fetchWithCache: async () => { throw new Error('Not implemented'); },
  clearApiCache: () => {},
});

// Custom hook to use the context
export const useAppContext = () => useContext(AppContext);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  // Load values from local storage or use defaults
  const [apiKey, setApiKey] = useState<string>(
    loadFromLocalStorage<string>('apiKey') || ''
  );
  
  // Theme state
  const [theme, setTheme] = useState<string>(
    loadFromLocalStorage<string>('theme') || 'light'
  );
  
  // Source backend settings
  const [sourceBackend, setSourceBackend] = useState<string>(
    loadFromLocalStorage<string>('sourceBackend') || 'tracking.cityflo.com'
  );
  
  const [sourcePort, setSourcePort] = useState<string>(
    loadFromLocalStorage<string>('sourcePort') || '8080'
  );
  
  const [isCustomSourceBackend, setIsCustomSourceBackend] = useState<boolean>(
    loadFromLocalStorage<boolean>('isCustomSourceBackend') || false
  );
  
  // Destination backend settings
  const [destBackend, setDestBackend] = useState<string>(
    loadFromLocalStorage<string>('destBackend') || 'tracking.cityflo.com'
  );
  
  const [destPort, setDestPort] = useState<string>(
    loadFromLocalStorage<string>('destPort') || '8080'
  );
  
  const [isCustomDestBackend, setIsCustomDestBackend] = useState<boolean>(
    loadFromLocalStorage<boolean>('isCustomDestBackend') || false
  );

  // Source connection status state
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('unknown');
  const [connectionError, setConnectionError] = useState<string>('');
  const [connectionTimestamp, setConnectionTimestamp] = useState<string>('');
  
  // Destination connection status state
  const [destConnectionStatus, setDestConnectionStatus] = useState<ConnectionStatus>('unknown');
  const [destConnectionError, setDestConnectionError] = useState<string>('');
  const [destConnectionTimestamp, setDestConnectionTimestamp] = useState<string>('');
  
  // API cache to prevent duplicate calls
  const apiCache = useRef<Map<string, { data: any; timestamp: number }>>(new Map());
  const cacheTTL = 30000; // 30 seconds cache TTL

  // Save values to local storage when they change
  useEffect(() => {
    saveToLocalStorage('apiKey', apiKey);
  }, [apiKey]);

  // Save source backend settings
  useEffect(() => {
    saveToLocalStorage('sourceBackend', sourceBackend);
  }, [sourceBackend]);

  useEffect(() => {
    saveToLocalStorage('sourcePort', sourcePort);
  }, [sourcePort]);

  useEffect(() => {
    saveToLocalStorage('isCustomSourceBackend', isCustomSourceBackend);
  }, [isCustomSourceBackend]);
  
  // Save destination backend settings
  useEffect(() => {
    saveToLocalStorage('destBackend', destBackend);
  }, [destBackend]);

  useEffect(() => {
    saveToLocalStorage('destPort', destPort);
  }, [destPort]);

  useEffect(() => {
    saveToLocalStorage('isCustomDestBackend', isCustomDestBackend);
  }, [isCustomDestBackend]);

  // Function to get the complete backend URL
  // Function to get the source backend URL
  const getSourceBackendUrl = (): string => {
    if (isCustomSourceBackend) {
      return `http://localhost:${sourcePort}`;
    }
    return `https://${sourceBackend}`;
  };
  
  // Function to get the destination backend URL
  const getDestBackendUrl = (): string => {
    if (isCustomDestBackend) {
      return `http://localhost:${destPort}`;
    }
    return `https://${destBackend}`;
  };
  
  // Legacy function for backward compatibility
  const getBackendUrl = (): string => {
    return getSourceBackendUrl();
  };

  // Function to validate connection with the source backend
  const validateConnection = async (): Promise<void> => {
    if (!apiKey) {
      setConnectionStatus('failed');
      setConnectionError('API key is required');
      return;
    }

    setConnectionStatus('connecting');
    setConnectionError('');

    try {
      // Use source backend for validation
      const baseUrl = getSourceBackendUrl();
      console.log(`Attempting to connect to source backend ${baseUrl} with API key: ${apiKey}`);
      
      // Make an actual network request to the backend
      const response = await fetch(`${baseUrl}/api/tools/tracking-sim/validate-connection/`, {
        method: 'GET',
        headers: {
          'x-tracking-sim-api-key': apiKey
        },
        mode: 'cors'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Source connection successful:', data);
        setConnectionStatus('connected');
        // Save the timestamp from the response if available
        if (data.timestamp) {
          setConnectionTimestamp(data.timestamp);
        }
      } else {
        let errorMessage = 'Connection failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse the JSON, just use the status text
          errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
        }
        console.error('Source connection failed:', errorMessage);
        setConnectionStatus('failed');
        setConnectionError(errorMessage);
      }
    } catch (error) {
      console.error('Source connection error:', error);
      setConnectionStatus('failed');
      setConnectionError(error instanceof Error ? error.message : 'Connection failed');
    }
  };
  
  // Function to validate connection with the destination backend
  const validateDestConnection = async (): Promise<void> => {
    if (!apiKey) {
      setDestConnectionStatus('failed');
      setDestConnectionError('API key is required');
      return;
    }

    setDestConnectionStatus('connecting');
    setDestConnectionError('');

    try {
      // Use destination backend for validation
      const baseUrl = getDestBackendUrl();
      console.log(`Attempting to connect to destination backend ${baseUrl} with API key: ${apiKey}`);
      
      // Make an actual network request to the backend
      const response = await fetch(`${baseUrl}/api/tools/tracking-sim/validate-connection/`, {
        method: 'GET',
        headers: {
          'x-tracking-sim-api-key': apiKey
        },
        mode: 'cors'
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Destination connection successful:', data);
        setDestConnectionStatus('connected');
        // Save the timestamp from the response if available
        if (data.timestamp) {
          setDestConnectionTimestamp(data.timestamp);
        }
      } else {
        let errorMessage = 'Connection failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          // If we can't parse the JSON, just use the status text
          errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
        }
        console.error('Destination connection failed:', errorMessage);
        setDestConnectionStatus('failed');
        setDestConnectionError(errorMessage);
      }
    } catch (error) {
      console.error('Destination connection error:', error);
      setDestConnectionStatus('failed');
      setDestConnectionError(error instanceof Error ? error.message : 'Connection failed');
    }
  };

  // Track previous values to detect changes
  const prevSourceApiKeyRef = useRef<string>(apiKey);
  const prevDestApiKeyRef = useRef<string>(apiKey);
  const prevSourceSettingsRef = useRef<string>(`${sourceBackend}-${sourcePort}-${isCustomSourceBackend}`);
  const prevDestSettingsRef = useRef<string>(`${destBackend}-${destPort}-${isCustomDestBackend}`);
  
  // Validate both backends on page refresh
  useEffect(() => {
    // Only validate if API key is present
    if (apiKey) {
      console.log('Page loaded/refreshed, validating both backends');
      
      // Validate source backend
      validateConnection();
      
      // Validate destination backend
      validateDestConnection();
      
      // Initialize refs with current values to prevent duplicate validations
      prevSourceApiKeyRef.current = apiKey;
      prevDestApiKeyRef.current = apiKey;
      prevSourceSettingsRef.current = `${sourceBackend}-${sourcePort}-${isCustomSourceBackend}`;
      prevDestSettingsRef.current = `${destBackend}-${destPort}-${isCustomDestBackend}`;
    }
    // Empty dependency array means this runs once on mount
  }, []);
  
  // Validate source connection when apiKey or source backend changes
  useEffect(() => {
    // Skip validation if no API key is set
    if (!apiKey) {
      prevSourceApiKeyRef.current = '';
      return;
    }
    
    // Create a string representation of current settings
    const currentSourceSettings = `${sourceBackend}-${sourcePort}-${isCustomSourceBackend}`;
    
    // Check if API key changed or source settings changed
    const apiKeyChanged = apiKey !== prevSourceApiKeyRef.current;
    const sourceSettingsChanged = currentSourceSettings !== prevSourceSettingsRef.current;
    
    // Only validate if something actually changed
    if (apiKeyChanged || sourceSettingsChanged) {
      // Use a timeout to debounce the validation call
      const debounceTimeout = setTimeout(() => {
        console.log('Validating source connection after input change');
        validateConnection();
      }, 500); // 500ms debounce
      
      // Update refs with current values
      prevSourceApiKeyRef.current = apiKey;
      prevSourceSettingsRef.current = currentSourceSettings;
      
      // Clean up the timeout if the dependencies change before the timeout fires
      return () => clearTimeout(debounceTimeout);
    }
  }, [apiKey, sourceBackend, sourcePort, isCustomSourceBackend]);
  
  // Validate destination connection when apiKey or destination backend changes
  useEffect(() => {
    // Skip validation if no API key is set
    if (!apiKey) {
      prevDestApiKeyRef.current = '';
      return;
    }
    
    // Create a string representation of current settings
    const currentDestSettings = `${destBackend}-${destPort}-${isCustomDestBackend}`;
    
    // Check if API key changed or destination settings changed
    const apiKeyChanged = apiKey !== prevDestApiKeyRef.current;
    const destSettingsChanged = currentDestSettings !== prevDestSettingsRef.current;
    
    // Only validate if something actually changed
    if (apiKeyChanged || destSettingsChanged) {
      // Use a timeout to debounce the validation call
      const debounceTimeout = setTimeout(() => {
        console.log('Validating destination connection after input change');
        validateDestConnection();
      }, 500); // 500ms debounce
      
      // Update refs with current values
      prevDestApiKeyRef.current = apiKey;
      prevDestSettingsRef.current = currentDestSettings;
      
      // Clean up the timeout if the dependencies change before the timeout fires
      return () => clearTimeout(debounceTimeout);
    }
  }, [apiKey, destBackend, destPort, isCustomDestBackend]);

  // Fetch with cache function to prevent duplicate API calls
  const fetchWithCache = async <T,>(url: string, options?: RequestInit): Promise<T> => {
    const cacheKey = `${url}-${JSON.stringify(options)}`;
    const now = Date.now();
    
    // Check if we have a valid cached response
    const cachedItem = apiCache.current.get(cacheKey);
    if (cachedItem && (now - cachedItem.timestamp < cacheTTL)) {
      console.log(`Using cached response for ${url}`);
      return cachedItem.data as T;
    }
    
    // If not in cache or expired, make the actual API call
    console.log(`Making API call to ${url}`);
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `API call failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Cache the response
    apiCache.current.set(cacheKey, { data, timestamp: now });
    
    return data as T;
  };
  
  // Function to clear the API cache
  const clearApiCache = () => {
    apiCache.current.clear();
    console.log('API cache cleared');
  };

  // Context value
  const value = {
    // Common settings
    apiKey,
    setApiKey,
    
    // Theme settings
    theme,
    setTheme,
    
    // Source backend settings
    sourceBackend,
    setSourceBackend,
    sourcePort,
    setSourcePort,
    isCustomSourceBackend,
    setIsCustomSourceBackend,
    getSourceBackendUrl,
    
    // Destination backend settings
    destBackend,
    setDestBackend,
    destPort,
    setDestPort,
    isCustomDestBackend,
    setIsCustomDestBackend,
    getDestBackendUrl,
    
    // Source connection status
    connectionStatus,
    connectionError,
    connectionTimestamp,
    validateConnection,
    
    // Destination connection status
    destConnectionStatus,
    destConnectionError,
    destConnectionTimestamp,
    validateDestConnection,
    
    // API utilities
    fetchWithCache,
    clearApiCache,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
